import { Video, Trash2 } from 'lucide-react';
import { Calendar as CalendarIcon, RefreshCw } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '../ui/table';

const BIBLE_STUDY_ZOOM = "https://us05web.zoom.us/j/81120587177";
const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const FONT_BODY = { fontFamily: "'Crimson Text', Georgia, serif" };

const getRowClassName = (isToday, isEven) => {
  if (isToday) return 'bg-yellow-50 border-l-4 border-l-yellow-500';
  return isEven ? 'bg-white' : 'bg-blue-50/30';
};

const EventRow = ({ dayData, isToday, isEven, adminVerified, onDelete }) => {
  const dateNum = dayData.date.getDate();
  const dayName = DAYS_OF_WEEK[dayData.date.getDay()];

  return (
    <TableRow
      data-testid={`event-row-${dateNum}`}
      className={`${getRowClassName(isToday, isEven)} hover:bg-blue-50/60 transition-colors`}
    >
      <TableCell className="py-3 px-3 md:px-6 align-top">
        <div className="text-center md:text-left">
          <span className={`text-xl md:text-2xl font-bold block ${isToday ? 'text-yellow-600' : 'text-blue-800'}`} style={FONT_BODY}>
            {dateNum}
          </span>
          <span className="text-blue-600 font-semibold text-xs md:hidden block" style={FONT_BODY}>{dayName}</span>
        </div>
      </TableCell>
      <TableCell className="py-3 px-6 align-top hidden md:table-cell">
        <span className="text-blue-700 font-semibold text-base" style={FONT_BODY}>{dayName}</span>
      </TableCell>
      <TableCell className="py-3 px-3 md:px-6">
        <div className="space-y-2">
          {dayData.events.map((event) => (
            <div key={event.id} className="flex items-start gap-2 md:gap-3 group">
              <div className="w-1.5 h-1.5 md:w-2 md:h-2 bg-yellow-500 rounded-full mt-2 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-1 md:gap-2 flex-wrap">
                  <span className="font-bold text-blue-900 text-sm md:text-base" style={FONT_BODY}>{event.title}</span>
                  <span className="text-yellow-700 font-semibold text-xs md:text-sm" style={FONT_BODY}>{event.time}</span>
                </div>
                {event.description && (
                  <p className="text-blue-600 text-xs md:text-sm mt-0.5 hidden md:block" style={FONT_BODY}>{event.description}</p>
                )}
                {(event.zoom_link || event.title.toLowerCase().includes('bible study')) && (
                  <a
                    href={event.zoom_link || BIBLE_STUDY_ZOOM}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`zoom-link-${event.id}`}
                    className="inline-flex items-center gap-1 text-xs md:text-sm text-blue-600 hover:text-blue-800 underline mt-0.5"
                    style={FONT_BODY}
                  >
                    <Video className="h-3 w-3 md:h-3.5 md:w-3.5" /> Zoom
                  </a>
                )}
              </div>
              {adminVerified && (
                <button
                  data-testid={`delete-event-${event.id}`}
                  onClick={() => onDelete(event)}
                  className="opacity-0 group-hover:opacity-100 transition-opacity p-1 text-red-400 hover:text-red-600"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      </TableCell>
    </TableRow>
  );
};

export const EventsTable = ({ calendarData, loading, currentMonth, currentYear, adminVerified, onDelete }) => {
  const today = new Date();

  if (loading) {
    return (
      <Card className="border-4 border-yellow-400 shadow-xl overflow-hidden">
        <CardContent className="p-0">
          <div className="flex items-center justify-center py-16">
            <RefreshCw className="h-8 w-8 text-blue-600 animate-spin" />
            <span className="ml-3 text-blue-700 text-lg">Loading events...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (calendarData.length === 0) {
    return (
      <Card className="border-4 border-yellow-400 shadow-xl overflow-hidden">
        <CardContent className="p-0">
          <div className="text-center py-16 px-4">
            <CalendarIcon className="h-12 w-12 text-blue-300 mx-auto mb-4" />
            <p className="text-blue-700 text-lg" style={FONT_BODY}>
              No events scheduled for {MONTHS[currentMonth]} {currentYear}.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-4 border-yellow-400 shadow-xl overflow-hidden">
      <CardContent className="p-0">
        <Table data-testid="events-table">
          <TableHeader>
            <TableRow className="bg-blue-800">
              <TableHead className="text-yellow-300 font-bold text-sm md:text-base py-3 px-3 md:px-6 w-[50px] md:w-[80px]" style={FONT_BODY}>Date</TableHead>
              <TableHead className="text-yellow-300 font-bold text-base py-3 px-6 w-[100px] hidden md:table-cell" style={FONT_BODY}>Day</TableHead>
              <TableHead className="text-yellow-300 font-bold text-sm md:text-base py-3 px-3 md:px-6" style={FONT_BODY}>Events</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {calendarData.map((dayData, idx) => {
              const isToday = dayData.date.getDate() === today.getDate() &&
                dayData.date.getMonth() === today.getMonth() &&
                dayData.date.getFullYear() === today.getFullYear();

              return (
                <EventRow
                  key={dayData.date.toISOString()}
                  dayData={dayData}
                  isToday={isToday}
                  isEven={idx % 2 === 0}
                  adminVerified={adminVerified}
                  onDelete={onDelete}
                />
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};
